import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';

// Get API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string for production (relative URL)
  : 'http://localhost:5001';

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { token: paramToken } = useParams();
  const token = paramToken || searchParams.get('token');
  const navigate = useNavigate();
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  
  const verifyEmail = async () => {
    try {
      setIsVerifying(true);
      const response = await fetch(`${API_BASE_URL}/api/verify/${token}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('verifyEmail.error', 'Failed to verify email'));
      }
      
      setIsVerified(true);
      if (data.email) setEmail(data.email);
      
      // Auto-redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { message: t('verifyEmail.successRedirect', 'Email verified successfully. You can now log in.') }
        });
      }, 3000);
    } catch (err) {
      setError(err.message || t('verifyEmail.serverError', 'Server error. Please try again later.'));
    } finally {
      setIsVerifying(false);
    }
  };
  
  useEffect(() => {
    if (token) {
      verifyEmail();
    } else {
      setIsVerifying(false);
      setError(t('verifyEmail.missingToken', 'Verification token is missing.'));
    }
  }, [token]);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-md mx-auto pt-16 px-4">
        <div className="bg-white p-8 border rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-6 text-center">
            {t('verifyEmail.title', 'Email Verification')}
          </h2>
          
          {isVerifying && (
            <div className="text-center py-4">
              <div className="animate-spin inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full mb-2"></div>
              <p>{t('verifyEmail.verifying', 'Verifying your email address...')}</p>
            </div>
          )}
          
          {!isVerifying && isVerified && (
            <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <p className="font-medium">{t('verifyEmail.successTitle', 'Email Verified!')}</p>
              <p>{t('verifyEmail.successMessage', 'Your email has been successfully verified.')}</p>
              <p className="mt-2">{t('verifyEmail.redirecting', 'Redirecting to login page...')}</p>
            </div>
          )}
          
          {!isVerifying && !isVerified && (
            <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              <p className="font-medium">{t('verifyEmail.failedTitle', 'Verification Failed')}</p>
              <p>{error}</p>
              <p className="mt-2">{t('verifyEmail.failedHelp', 'The verification link may have expired or is invalid.')}</p>
            </div>
          )}
          
          <div className="mt-6 text-center">
            <Link to="/login" className="text-blue-600 hover:underline">
              {t('verifyEmail.login', 'Go to Login')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage; 